import React, { memo, useRef } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { reExportedColors, Input, Text } from '../1elements/';
import QuickView from '../../v2/components/QuickView';
import { useMapSearch } from '../../services/mapSearch/useMapSearch';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import AscendIcon from '../1elements/Icons/js/AscendIcon';
import { ITooltip, Loader } from '../1elements';
import CustomMapMarker from '../../v2/components/CustomMapMarker';
import { texts } from '../../consts/Texts';
import MapViewSelect from '../2compounds/Select/MapViewSelect';
import MapSearchRangeCheckBox from '../2compounds/Select/MapSearchRangeCheckBox';
import GeoZoneSelect from '../GeoZoneSelect';
import ExecutiveGroupSelect from '../ExecutiveGroupSelect';
import ExecutiveEntityQuickView from '../../v2/components/ExecutiveEntityQuickView';
import { SEARCH_OPTIONS } from '../../consts/g-map-search'

import { useFetchHumanReadableAddress } from '../../v2/hooks/use-fetch-human-readable-address.hook';

function MapSearch({ animation }) {
  const {
    searchLoading,
    sortedSearchSuggestions,
    getBuilding,
    handleSearch,
    clearSearch,
    handleKeypress,
    showQuickView,
    setShowQuickView,
    quickViewData,
    allowed,
    showRes,
    setShowRes,
    showAll,
    showAllResults,
    searchText,
    searchOption,
    setSearchOption,
    zoneId,
    onZonePicked,
    executiveGroupId,
    onExecutivePicked,
    executiveEntity,
    setExecutiveEntity,
    toggleCollapseIsOpen,
    collapseIsOpen,
    sortAsc,
    toggleSort
  } = useMapSearch();
  const classes = useStyles({ collapseIsOpen });
  const resize = useMediaQuery('(max-width:750px)');
  const inputRef = useRef(null);
  const { populatedWithAddresses } = useFetchHumanReadableAddress(
    sortedSearchSuggestions
  );

  const displaySuggestions = () => {
    return populatedWithAddresses.map((place, index) => {
      const {
        name,
        building_address,
        humanReadableAddress,
        loc_name,
        location_address
      } = place.value[0];
      const address = building_address || humanReadableAddress;

      return (
        <div
          className={classes.query}
          key={index}
          onClick={() => getBuilding(place)}
        >
          {name === null ? (
            <>
              <Text type="searchResultMain">{loc_name}</Text>
              <Text type="searchResultSub">{location_address}</Text>
            </>
          ) : (
            <>
              <Text type="searchResultMain">{name}</Text>
              <Text type="searchResultSub">{address}</Text>
            </>
          )}
        </div>
      );
    });
  };

  const renderLessSuggestions = () => {
    return sortedSearchSuggestions.slice(0, 5).map((place, index) => {
      const address =
        place.value[0].building_address || place.value[0].humanReadableAddress;

      return (
        <div
          className={classes.query}
          key={index}
          onClick={() => getBuilding(place)}
        >
          {place.value[0].name === null ? (
            <>
              <Text type="searchResultMain">{place.value[0].loc_name}</Text>
              <Text type="searchResultSub">
                {place.value[0].location_address}
              </Text>
            </>
          ) : (
            <>
              <Text type="searchResultMain">{place.value[0].name}</Text>
              <Text type="searchResultSub">{address}</Text>
            </>
          )}
        </div>
      );
    });
  };

  const handleCloseQuickView = () => {
    setShowQuickView(false);
    setShowRes(true);
  };

  const handleCloseExecutiveEntity = () => {
    setExecutiveEntity(null);
  };

  const handleClearSearch = () => {
    clearSearch();

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <>
      <div className={classes.root}>
        <div className={classes.searchControlsRow}>
          <div className={classes.searchDiv}>
            {searchOption === SEARCH_OPTIONS.ZONE ? (
              <GeoZoneSelect
                value={zoneId}
                onChange={onZonePicked}
              />
            ) : searchOption === SEARCH_OPTIONS.EXECUTIVE ? (
              <ExecutiveGroupSelect
                value={executiveGroupId}
                onChange={onExecutivePicked}
              />
            ) : (
              <Input
                inputRef={inputRef}
                autoComplete="off"
                name="q"
                value={searchText || ''}
                onChange={handleSearch}
                onClear={handleClearSearch}
                variant="outlined"
                animation={animation}
                type="search"
                id="search-location"
                placeholder={texts.searchText}
                onKeyDown={handleKeypress}
              />
            )}
            <MapViewSelect
              value={searchOption}
              onChange={(v) => {
                const value = v?.target?.value ?? v;
                if(typeof value === 'string') {
                  setSearchOption(value);
                }
              }}
            />
            {(searchOption !== SEARCH_OPTIONS.ZONE &&
              searchOption !== SEARCH_OPTIONS.EXECUTIVE) && (
              <MapSearchRangeCheckBox className={classes.shadow} />
            )}
          </div>

        </div>
      </div>

      {executiveEntity ? (
        <ExecutiveEntityQuickView
          entity={executiveEntity}
          onClose={handleCloseExecutiveEntity}
        />
      ) : showQuickView ? (
        <QuickView
          mapSearchData={quickViewData}
          permissionGiven={allowed}
          loading={searchLoading}
          onClose={handleCloseQuickView}
        />
      ) : (
        showRes &&
        searchText.length > 0 && (
          <div
            className={`${classes.autocomplete} ${
              resize && classes.autocompleteMobile
            }`}
          >
            <div className={classes.innerDiv}>
              <Collapse in={collapseIsOpen} timeout="auto" unmountOnExit>
                <div className={classes.headerDiv}>
                  <Text type="h9">
                    <span className={classes.resultCount}>
                      {sortedSearchSuggestions.length} results
                    </span>
                  </Text>
                  <>
                    <ITooltip content="Sort" placement="top">
                      <AscendIcon isAscend={sortAsc} onClick={toggleSort} />
                    </ITooltip>

                    <CustomMapMarker />
                  </>
                </div>
                <Divider />
                {sortedSearchSuggestions.length === 0 ? (
                  <Text className={classes.noResultMessage} type="p">
                    We couldn&apos;t find any results. Please try again.
                  </Text>
                ) : sortedSearchSuggestions.length >= 2 ? (
                  <>
                    <div
                      className={showAll ? classes.hideThreeResult : undefined}
                    >
                      <div>{renderLessSuggestions()}</div>
                      <Divider />
                      {sortedSearchSuggestions.length >= 6 && (
                        <div
                          className={classes.viewMore}
                          onClick={showAllResults}
                        >
                          <Text type="p" className={classes.viewMoreText}>
                            View all results <ArrowDropDownIcon />
                          </Text>
                        </div>
                      )}
                    </div>
                    <div>{showAll && displaySuggestions()}</div>
                  </>
                ) : (
                  displaySuggestions()
                )}
              </Collapse>
            </div>
            <div
              className={classes.collapseButton}
              onClick={toggleCollapseIsOpen}
            >
              <ArrowDropDownIcon className={classes.collapseButtonIcon} />
            </div>
          </div>
        )
      )}

      {searchLoading && <Loader />}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    top: '20px',
    left: 0,
    width: '100%',
    padding: '0 2rem'
  },
  searchControlsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  autocomplete: {
    fontSize: 13,
    width: '430px',
    border: 'none',
    boxShadow: '0px 0px 6px #00000029',
    borderRadius: '4px',
    boxSizing: 'border-box',
    marginTop: '4px',
    maxHeight: '400px',
    overflowX: 'hidden',
    fontFamily: 'Roboto',
    background: theme.palette.quaternary.main,
    position: 'absolute',
    top: '70px',
    left: '30px',
    zIndex: 10
  },
  autocompleteMobile: {
    boxShadow: '0px 0px 3px #00000029',
    margin: '4px 0 0 -10px'
  },
  secondSearch: {
    width: 'fit-content',
    height: '40px',
    padding: '10px 15px 0',
    top: '6px',
    left: '40rem',
    zIndex: '10',
    textAlign: 'center',
    fontSize: '14px',
    fontWeight: '500',
    border: 'none',
    backgroundColor: reExportedColors.WHITE, /// /// HARDCODED TO USE CONSTS - to be Assigned a palette color
    position: 'absolute',
    borderRadius: '40px',
    boxShadow: '0px 0px 3px #00000029',
    cursor: 'pointer'
  },
  secondSearchIcon: {
    height: 20,
    width: 20,
    color: reExportedColors.GOOGLE_BLUE,
    margin: '0 4px 4px 0'
  },
  query: {
    borderBottom: `1px solid ${reExportedColors.LIGHT_GREY}`, /// /// HARDCODED TO USE CONSTS - to be Assigned a palette color
    padding: '8px',
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.secondary.main
    }
  },
  locIcons: {
    width: '1em',
    height: '17px'
  },
  threeResult: {
    paddingTop: 8
  },
  hideThreeResult: {
    display: 'none'
  },
  subResult: {
    borderRadius: '4px',
    cursor: 'pointer',
    '&:hover': {
      background: reExportedColors.HOVER /// /// HARDCODED TO USE CONSTS - to be Assigned a palette color
    }
  },
  searchDiv: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    zIndex: 10,

    '& .MuiFormControl-root': {
      borderRadius: '5px',
      boxShadow: '1px 1px 5px 0px #00000040'
    }
  },
  viewMore: {
    margin: '7px 0',
    padding: '3px 0',
    textAlign: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
    '&:hover': {
      background: reExportedColors.HOVER /// /// HARDCODED TO USE CONSTS - to be Assigned a palette color
    }
  },
  viewMoreText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px'
  },
  headerDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    top: 0,
    height: '45px',
    padding: 0,
    position: 'relative'
  },
  resultCount: {
    padding: '8px'
  },
  noResultMessage: {
    padding: '8px',
    display: 'inline-block'
  },
  collapseButton: {
    background: theme.palette.quaternary.accent,
    position: 'sticky',
    bottom: 0,
    left: 0,
    width: '100%',
    cursor: 'pointer',
    height: '15px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflowY: 'hidden'
  },
  collapseButtonIcon: {
    fontSize: '26px',
    transform: (props) =>
      props.collapseIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform .25s',
    color: theme.palette.primary.main
  },
  innerDiv: {
    overflowY: 'auto',
    maxHeight: (props) => (props.collapseIsOpen ? '385px' : '0'),
    padding: 0,
    transition: 'max-height .25s, padding .25s'
  },
  advancedSearch: {
    position: 'absolute',
    left: '435px',
    zIndex: 2
  },
  shadow: {
    borderRadius: '5px',
    boxShadow: '1px 1px 5px 0px #00000040'
  }
}));

export default memo(MapSearch);
